import { Box, IconButton, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

interface TablePaginationProps {
  pageIndex: number
  pageCount: number
  totalRows: number
  pageSize: number
  gotoPage: (page: number) => void
}

const TablePagination = ({
  pageIndex,
  pageCount,
  totalRows,
  pageSize,
  gotoPage,
}: TablePaginationProps) => {
  const startRow = pageIndex * pageSize + 1
  const endRow = Math.min((pageIndex + 1) * pageSize, totalRows)

  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisiblePages = 5

    if (pageCount <= maxVisiblePages) {
      for (let i = 0; i < pageCount; i++) {
        pages.push(i)
      }
    } else {
      pages.push(0)

      if (pageIndex > 2) {
        pages.push('...')
      }

      const start = Math.max(1, pageIndex - 1)
      const end = Math.min(pageCount - 2, pageIndex + 1)

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i)
        }
      }

      if (pageIndex < pageCount - 3) {
        pages.push('...')
      }

      if (!pages.includes(pageCount - 1)) {
        pages.push(pageCount - 1)
      }
    }

    return pages
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Showing {startRow} – {endRow} of {totalRows} customers
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => gotoPage(pageIndex - 1)}
          disabled={pageIndex === 0}
          sx={{
            width: 32,
            height: 32,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-disabled': { color: 'action.disabled' },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        {getPageNumbers().map((page, index) =>
          page === '...' ? (
            <Typography
              key={`ellipsis-${index}`}
              sx={{ px: 1, color: 'text.disabled' }}
            >
              ...
            </Typography>
          ) : (
            <IconButton
              key={page}
              size="small"
              onClick={() => gotoPage(page as number)}
              sx={{
                width: 32,
                height: 32,
                fontSize: 13,
                fontWeight: 500,
                borderRadius: 1,
                ...(pageIndex === page
                  ? {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }
                  : {
                      color: 'text.secondary',
                      '&:hover': { bgcolor: 'action.hover' },
                    }),
              }}
            >
              {(page as number) + 1}
            </IconButton>
          )
        )}

        <IconButton
          size="small"
          onClick={() => gotoPage(pageIndex + 1)}
          disabled={pageIndex >= pageCount - 1}
          sx={{
            width: 32,
            height: 32,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-disabled': { color: 'action.disabled' },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  )
}

export default TablePagination
